/**
 * StitchSet — issue #810, epic #802. Extended for click stitches in #963.
 *
 * First-class, persisted artifact representing two layers of explicit
 * mappings:
 *
 *   1. **URL stitches** (legacy / original): frontend ApiCallSites →
 *      backend RouteEndpoints. The "is this fetch hitting that route?"
 *      decision is recorded here rather than guessed by URL-string matching.
 *   2. **Click stitches** (#963): frontend user-interaction handlers →
 *      api-calls. The "does this onClick fire that fetch?" decision is
 *      recorded here rather than guessed by name-based heuristics. Same
 *      provenance model: auto / ai / user.
 *
 * Both stitch kinds carry the same source-tracking semantics:
 *   - auto: emitted by the auto-stitcher (#811 for url, #963 for click)
 *           from exact / structural matches
 *   - user: confirmed/created by a human in the review UI (#812)
 *   - ai:   proposed by the LLM pipeline (#813 for url, #963 for click),
 *           awaiting user review
 *
 * The StitchSet is persisted as `.veodiagram/stitches.json` in the
 * analyzed repo. On re-analysis, the structural keys are matched
 * against the fresh FCG/BCG/UserInteraction set to reattach stitches
 * even when files have moved or functions have been renamed.
 *
 * Backward compatibility (#963): older `.stitches.json` files persisted
 * before the click-stitch split carried `stitches: { auto, user, ai }`
 * (flat). The loader migrates that shape to `stitches.url.{auto,user,ai}`
 * with empty `stitches.click` lists.
 */
import { z } from 'zod';
import { CallSiteKeySchema } from './frontendCallGraph.js';
import { RouteKeySchema } from './backendCallGraph.js';
// ============= Common =============
export const StitchSourceSchema = z.enum(['auto', 'user', 'ai']);
// ============= Click stitch keys (#963) =============
/**
 * Structural key for a user-interaction handler. Stable across
 * re-analyses because it leans on file path + function name +
 * trigger element rather than the synthesized interaction id which
 * shifts when the detector re-numbers nodes.
 */
export const ClickStitchHandlerKeySchema = z.object({
    /** Repo-relative, normalized with forward slashes. */
    handlerFile: z.string(),
    /** Handler function name (e.g. 'handleClick'). */
    handlerFunction: z.string(),
    /** JSX trigger element (e.g. 'button', 'div', 'path'). */
    triggerElement: z.string(),
    /** Event type (e.g. 'onClick', 'onSubmit'). */
    eventType: z.string(),
    /** Enclosing component name when known. Optional — anonymous-arrow
     *  handlers in passing-through cases don't always have one. */
    component: z.string().optional(),
});
/**
 * Structural key for an api-call site. Lighter than CallSiteKey
 * (used for url stitches at the FCG layer) — click stitches don't
 * need the wrapper-chain hash because the disambiguation lives in
 * the handler key. Keyed on (file, method, endpoint, sourceContext)
 * which is enough to survive line-shift across analyses.
 */
export const ApiCallKeySchema = z.object({
    sourceFile: z.string(),
    method: z.string(),
    endpoint: z.string(),
    /** Enclosing function name (sourceContext from ApiCall). Optional. */
    sourceContext: z.string().optional(),
});
// ============= Stitch (discriminated union) =============
/**
 * #1280 PR 2/11 — bucketed confidence.
 *
 *   - `evident`  — literal/structural match (exact URL match, parameterized
 *                  exact, user/AI confirmed). Classifier treats as confirmed.
 *   - `inferred` — rule-based match where the rule cannot fully confirm
 *                  (suffix-only match, hop-distance match). Classifier
 *                  surfaces these for review.
 *   - `proposed` — AI / heuristic that has NOT been confirmed. Classifier
 *                  never auto-promotes these.
 *
 * The numeric `confidence` field is retained for one release to avoid
 * breaking persisted stitch records and downstream sort orders. Reads
 * prefer the bucket when present; writers set BOTH so consumers can
 * migrate at their own pace.
 *
 * Bucket-from-numeric fallback (used at load time when bucket is absent):
 *   confidence ≥ 0.9            → `evident`
 *   confidence ≥ 0.7            → `inferred`
 *   confidence <  0.7            → `proposed`
 */
export const ConfidenceBucketSchema = z.enum(['evident', 'inferred', 'proposed']);
const StitchCommonShape = {
    /** Confidence in the mapping, 0..1. Retained for backwards compatibility
     *  with persisted records; new code should prefer `confidenceBucket`. */
    confidence: z.number().min(0).max(1),
    /** #1280 PR 2 — discrete confidence bucket. Optional during the migration
     *  window so older persisted stitches still load. Writers set BOTH;
     *  readers prefer this when present. */
    confidenceBucket: ConfidenceBucketSchema.optional(),
    /** Origin of the stitch. */
    source: StitchSourceSchema,
    /** Human- or machine-readable rationale for the match. */
    rationale: z.string(),
    /** ISO 8601 timestamp of creation. */
    createdAt: z.string(),
    /** Optional — user ID for 'user' acceptance, or AI model id for 'ai'. */
    createdBy: z.string().optional(),
    /** Chain-of-thought reasoning from the AI (reasoning models only).
     *  Shown in the review modal so the human can evaluate the AI's
     *  logic before accepting or rejecting. */
    aiReasoning: z.string().optional(),
};
/**
 * Map a numeric confidence to a bucket. Used by the loader for legacy
 * stitches that were persisted before `confidenceBucket` was added, and
 * by classifier code paths that need to compare a fresh-from-autoStitcher
 * record (which sets `confidence` but not yet a bucket) against the
 * bucket-aware decision.
 */
export function bucketFromConfidence(confidence) {
    if (confidence >= 0.9)
        return 'evident';
    if (confidence >= 0.7)
        return 'inferred';
    return 'proposed';
}
/**
 * Read the bucket from a stitch with the legacy-numeric fallback rolled in.
 * Centralised so callers don't reimplement the threshold.
 */
export function bucketOf(stitch) {
    return stitch.confidenceBucket ?? bucketFromConfidence(stitch.confidence);
}
export const UrlStitchSchema = z.object({
    kind: z.literal('url'),
    /** Structural key of the call site (the fetch / axios call). */
    callSite: CallSiteKeySchema,
    /** Structural key of the route this call site maps to. */
    route: RouteKeySchema,
    ...StitchCommonShape,
});
export const ClickStitchSchema = z.object({
    kind: z.literal('click'),
    /** Structural key of the user-interaction handler. */
    handler: ClickStitchHandlerKeySchema,
    /** Structural key of the api-call this click is asserted to trigger. */
    apiCall: ApiCallKeySchema,
    ...StitchCommonShape,
});
/**
 * #963: Stitch is a discriminated union. The `kind` field is required
 * on new stitches; the loader injects it during legacy migration so
 * downstream code can rely on it always being present.
 */
export const StitchSchema = z.discriminatedUnion('kind', [
    UrlStitchSchema,
    ClickStitchSchema,
]);
// ============= Rule (URL only — #810 layer) =============
export const StitchRuleMatchKindSchema = z.enum([
    'wrapperChain',
    'urlPattern',
    'enclosingSymbol',
]);
export const StitchRuleTargetKindSchema = z.enum(['pathSuffix', 'exact', 'regex']);
export const StitchRuleSchema = z.object({
    id: z.string(),
    match: z.object({
        kind: StitchRuleMatchKindSchema,
        /** Glob-like pattern, e.g. 'apiClient.users.*' */
        pattern: z.string(),
    }),
    target: z.object({
        kind: StitchRuleTargetKindSchema,
        /** Route path pattern, e.g. '/api/users/*' */
        pattern: z.string(),
        /** Optional HTTP method filter. */
        method: z.string().optional(),
    }),
    confidence: z.number().min(0).max(1).default(0.9),
    createdBy: z.string(),
    createdAt: z.string(),
    /** Free-form note shown in the review UI. */
    note: z.string().optional(),
});
// ============= StitchSet =============
const StitchKindBucketSchema = (_kind, schema) => z.object({
    auto: z.array(schema).default([]),
    user: z.array(schema).default([]),
    ai: z.array(schema).default([]),
    /**
     * #1289 / #1280 PR 9 — pending AI proposals.
     *
     * Holds AI-generated stitch suggestions that have NOT been
     * confirmed by a user. The classifier IGNORES this bucket — only
     * `auto` / `user` / `ai` (= confirmed) affect flow classification.
     *
     * Lifecycle:
     *   1. `POST /projects/:id/stitches/propose { dryRun: true }`
     *      writes new proposals here instead of merging into `ai`.
     *   2. UI surfaces them in a review modal.
     *   3. A confirm endpoint promotes proposal → `ai`.
     *   4. A reject endpoint removes from `proposals`.
     *
     * Until the UX flip lands, the existing `propose` endpoint
     * continues to auto-merge into `ai` (for backwards compatibility);
     * the `dryRun` flag is the opt-in path.
     *
     * Default empty: older `stitches.json` files load unchanged.
     */
    proposals: z.array(schema).default([]),
});
export const UrlStitchBucketSchema = StitchKindBucketSchema('url', UrlStitchSchema);
/**
 * Click-stitch bucket includes an `ignored` list (#971): handlers
 * the user has explicitly marked as "UI-only" (no api-call to
 * stitch).
 *
 * Asymmetry-by-design with `StitchSet.ignored` (top-level,
 * `CallSiteKey[]`): the url-stitch ignore unit is a call site, the
 * click-stitch ignore unit is a handler. Different keys → different
 * homes. Nesting `click.ignored` under the bucket also keeps the
 * persisted shape per-kind, so a future third stitch kind can have
 * its own ignored list without polluting the top-level field.
 *
 * Backward-compat: default `[]` so older `stitches.json` files load
 * without the field.
 */
export const ClickStitchBucketSchema = z.object({
    auto: z.array(ClickStitchSchema).default([]),
    user: z.array(ClickStitchSchema).default([]),
    ai: z.array(ClickStitchSchema).default([]),
    /** #1289 PR 9 — pending AI proposals; mirror of url-bucket `proposals`. */
    proposals: z.array(ClickStitchSchema).default([]),
    ignored: z.array(ClickStitchHandlerKeySchema).default([]),
});
export const StitchSetSchema = z.object({
    version: z.literal(1),
    projectId: z.string(),
    generatedAt: z.string(),
    /**
     * #963: nested shape splits stitches by kind. The legacy on-disk
     * shape (`stitches: { auto, user, ai }`, all url) is migrated to
     * `stitches: { url: { ... }, click: { auto: [], user: [], ai: [] } }`
     * by `readStitchSet`'s normalisation pass. Writers always emit the
     * new shape.
     */
    stitches: z.object({
        url: UrlStitchBucketSchema,
        click: ClickStitchBucketSchema,
    }),
    rules: z.array(StitchRuleSchema).default([]),
    /** Call-site keys the user has explicitly marked as "not an API call". */
    ignored: z.array(CallSiteKeySchema).default([]),
});
//# sourceMappingURL=stitchSet.js.map